import { Trans, useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, cn } from '@pantry/ui';
import { useAdminMetrics, type MetricsOverview } from '@pantry/api-client';
import {
  UsersIcon,
  HomeIcon,
  ArchiveBoxIcon,
  ArrowTrendingUpIcon,
  ChevronRightIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  loading,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: { value: number; label: string };
  loading?: boolean;
}) {
  const { i18n } = useLingui();
  return (
    <Card className="rounded-xl">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-8 w-20 animate-pulse rounded bg-muted" />
        ) : (
          <div className="text-2xl font-bold">
            {typeof value === 'number' ? value.toLocaleString(i18n.locale) : value}
          </div>
        )}
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        {trend && (
          <div className="flex items-center gap-1 mt-2">
            <ArrowTrendingUpIcon
              className={cn(
                'h-3 w-3',
                trend.value >= 0 ? 'text-green-500' : 'text-red-500 rotate-180'
              )}
            />
            <span
              className={cn(
                'text-xs font-medium',
                trend.value >= 0 ? 'text-green-600' : 'text-red-600'
              )}
            >
              {trend.value >= 0 ? '+' : ''}
              {trend.value}
            </span>
            <span className="text-xs text-muted-foreground">{trend.label}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QuickAction({
  title,
  description,
  href,
  icon: Icon,
}: {
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Link
      to={href}
      className="flex items-center gap-4 rounded-xl border border-border bg-card p-4 hover:bg-muted/50 transition-colors group"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <ChevronRightIcon className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
    </Link>
  );
}

function ActivityRow({
  label,
  value,
  loading,
}: {
  label: string;
  value: number;
  loading?: boolean;
}) {
  const { i18n } = useLingui();
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      {loading ? (
        <div className="h-5 w-10 animate-pulse rounded bg-muted" />
      ) : (
        <span className="font-medium">{value.toLocaleString(i18n.locale)}</span>
      )}
    </div>
  );
}

export function AdminDashboard() {
  const { t, i18n } = useLingui();
  const { data: metrics, isLoading, error } = useAdminMetrics();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
          <ArrowTrendingUpIcon className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-foreground">
            <Trans>Dashboard</Trans>
          </h1>
          <p className="text-muted-foreground">
            <Trans>System overview and quick actions</Trans>
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-4 text-destructive">
          <Trans>Failed to load metrics: {error.message}</Trans>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t`Total Users`}
          value={metrics?.users.total || 0}
          subtitle={metrics ? t`+${metrics.users.thisMonth} this month` : undefined}
          icon={UsersIcon}
          loading={isLoading}
        />
        <StatCard
          title={t`Households`}
          value={metrics?.households.total || 0}
          subtitle={
            metrics
              ? t`~${metrics.households.avgMembersPerHousehold.toFixed(1)} members avg`
              : undefined
          }
          icon={HomeIcon}
          loading={isLoading}
        />
        <StatCard
          title={t`Total Items`}
          value={metrics?.items.total || 0}
          subtitle={metrics ? t`+${metrics.items.addedToday} today` : undefined}
          icon={ArchiveBoxIcon}
          loading={isLoading}
        />
        <StatCard
          title={t`Active Today`}
          value={metrics?.activity.activeToday || 0}
          subtitle={metrics ? t`${metrics.activity.activeThisWeek} this week` : undefined}
          icon={ClockIcon}
          loading={isLoading}
        />
      </div>

      {/* Two column layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* User activity */}
        <Card className="rounded-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UsersIcon className="h-5 w-5" />
              <Trans>User Activity</Trans>
            </CardTitle>
            <CardDescription>
              <Trans>Recent user registration and login activity</Trans>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <ActivityRow
                label={t`New users today`}
                value={metrics?.users.today || 0}
                loading={isLoading}
              />
              <ActivityRow
                label={t`New users this week`}
                value={metrics?.users.thisWeek || 0}
                loading={isLoading}
              />
              <ActivityRow
                label={t`New users this month`}
                value={metrics?.users.thisMonth || 0}
                loading={isLoading}
              />
              <ActivityRow
                label={t`Active this month`}
                value={metrics?.activity.activeThisMonth || 0}
                loading={isLoading}
              />
            </div>

            {/* Role breakdown */}
            {metrics && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm font-medium mb-3">
                  <Trans>Users by role</Trans>
                </p>
                <div className="flex gap-3">
                  <RoleBadge
                    role="superadmin"
                    count={metrics.users.byRole.superadmin || 0}
                    color="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                  />
                  <RoleBadge
                    role="admin"
                    count={metrics.users.byRole.admin || 0}
                    color="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                  />
                  <RoleBadge
                    role="user"
                    count={metrics.users.byRole.user || 0}
                    color="bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Item alerts */}
        <Card className="rounded-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <Trans>Item Alerts</Trans>
            </CardTitle>
            <CardDescription>
              <Trans>Items that need attention across all households</Trans>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <ClockIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="font-medium">
                      <Trans>Expiring Soon</Trans>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <Trans>Within 7 days</Trans>
                    </p>
                  </div>
                </div>
                {isLoading ? (
                  <div className="h-6 w-12 animate-pulse rounded bg-muted" />
                ) : (
                  <span className="text-lg font-semibold text-amber-600 dark:text-amber-400">
                    {metrics?.items.expiringSoon.toLocaleString(i18n.locale) || 0}
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="font-medium">
                      <Trans>Expired</Trans>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <Trans>Past expiry date</Trans>
                    </p>
                  </div>
                </div>
                {isLoading ? (
                  <div className="h-6 w-12 animate-pulse rounded bg-muted" />
                ) : (
                  <span className="text-lg font-semibold text-red-600 dark:text-red-400">
                    {metrics?.items.expired.toLocaleString(i18n.locale) || 0}
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick actions */}
      <Card className="rounded-xl">
        <CardHeader>
          <CardTitle>
            <Trans>Quick Actions</Trans>
          </CardTitle>
          <CardDescription>
            <Trans>Common administrative tasks</Trans>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2">
            <QuickAction
              title={t`Manage Users`}
              description={t`View and manage user accounts`}
              href="/admin/users"
              icon={UsersIcon}
            />
            <QuickAction
              title={t`View Statistics`}
              description={t`Detailed usage analytics`}
              href="/admin/dashboard"
              icon={ArrowTrendingUpIcon}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function RoleBadge({ role, count, color }: { role: string; count: number; color: string }) {
  const { t } = useLingui();

  const getRoleLabel = (role: string): string => {
    switch (role) {
      case 'superadmin':
        return t`Super`;
      case 'admin':
        return t`Admin`;
      case 'moderator':
        return t`Moderator`;
      default:
        return role;
    }
  };

  return (
    <div className={cn('rounded-lg px-3 py-1.5 text-sm font-medium', color)}>
      {getRoleLabel(role)}: {count}
    </div>
  );
}
